import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  UserX, 
  Plus, 
  Trash2, 
  Mail, 
  Loader2,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const colors = {
  dark: '#0D2440',
  blue: '#2E5E99',
  gray: '#5A7A9B',
  border: '#C8D8E8',
  bg: '#E7F0FA',
};

export default function ExclusionList() {
  const { token, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  // Set up axios auth header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/exclusion-list`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching exclusion list:', error);
      toast.error('Fehler beim Laden der Ausschlussliste');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleAddEmail = async (e) => {
    e.preventDefault();
    
    if (!newEmail.trim()) {
      toast.error('Bitte eine E-Mail-Adresse eingeben');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      toast.error('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }

    setIsAdding(true);
    try {
      await axios.post(`${API}/exclusion-list`, {
        email: newEmail.trim().toLowerCase(),
        note: newNote.trim() || null
      });
      toast.success('E-Mail-Adresse hinzugefügt');
      setNewEmail('');
      setNewNote('');
      fetchEntries();
    } catch (error) {
      console.error('Error adding email:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!entryToDelete) return;

    try {
      await axios.delete(`${API}/exclusion-list/${entryToDelete.id}`);
      toast.success('E-Mail-Adresse entfernt');
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Entfernen');
    }
  };

  const isAdmin = user?.is_admin;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: colors.dark }}>
            <UserX className="h-7 w-7" style={{ color: colors.blue }} />
            Ausschlussliste
          </h1>
          <p className="text-sm mt-1" style={{ color: colors.gray }}>
            E-Mail-Adressen von Administratoren, die aus den Reports ausgeschlossen werden sollen
          </p>
        </div>

      {/* Info Box */}
      <Card className="mb-6 bg-blue-50" style={{ borderColor: colors.blue }}>
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 mt-0.5" style={{ color: colors.blue }} />
            <div className="text-sm" style={{ color: colors.dark }}>
              <p className="font-medium mb-1">So funktioniert die Ausschlussliste:</p>
              <ul className="list-disc list-inside space-y-1" style={{ color: colors.gray }}>
                <li>Alle hier eingetragenen E-Mail-Adressen werden aus den Reports herausgefiltert</li>
                <li>Die Filterung erfolgt global für alle Report-Typen</li>
                <li>Im Report wird angezeigt, wie viele Personen ausgeschlossen wurden</li>
                <li>Mit dem Schalter "Liste ignorieren" können Sie die Filterung temporär deaktivieren</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Form (Admin only) */}
      {isAdmin && (
        <Card className="mb-6 bg-white" style={{ borderColor: colors.border }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: colors.dark }}>
              <Plus className="h-5 w-5" style={{ color: colors.blue }} />
              E-Mail-Adresse hinzufügen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddEmail} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail-Adresse *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: colors.gray }} />
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@example.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="pl-10"
                      style={{ borderColor: colors.border }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="note">Notiz (optional)</Label>
                  <Input
                    id="note"
                    type="text"
                    placeholder="z.B. IT-Administrator"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    style={{ borderColor: colors.border }}
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={isAdding || !newEmail.trim()}
                style={{ background: colors.dark }}
              >
                {isAdding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird hinzugefügt...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Hinzufügen
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List of Entries */}
      <Card className="bg-white" style={{ borderColor: colors.border }}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: colors.dark }}>
              <UserX className="h-5 w-5" style={{ color: colors.blue }} />
              Ausgeschlossene E-Mail-Adressen
              <Badge variant="secondary">{entries.length}</Badge>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.blue }} />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8" style={{ color: colors.gray }}>
              <UserX className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Keine E-Mail-Adressen in der Ausschlussliste</p>
              {isAdmin && (
                <p className="text-sm mt-1">Fügen Sie oben die erste E-Mail-Adresse hinzu</p>
              )}
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-gray-50 hover:bg-gray-100 transition-colors"
                    style={{ borderColor: colors.border }}
                  >
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5" style={{ color: colors.blue }} />
                      <div>
                        <p className="font-medium" style={{ color: colors.dark }}>
                          {entry.email}
                        </p>
                        <div className="flex items-center gap-2 text-xs" style={{ color: colors.gray }}>
                          {entry.note && (
                            <span className="bg-blue-100 px-2 py-0.5 rounded">{entry.note}</span>
                          )}
                          <span>Hinzugefügt von {entry.added_by}</span>
                          <span>•</span>
                          <span>{new Date(entry.added_at).toLocaleDateString('de-DE')}</span>
                        </div>
                      </div>
                    </div>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(entry)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              E-Mail-Adresse entfernen
            </DialogTitle>
            <DialogDescription>
              Möchten Sie die E-Mail-Adresse <strong>{entryToDelete?.email}</strong> wirklich aus der Ausschlussliste entfernen?
              <br /><br />
              Diese Person wird dann wieder in den Reports angezeigt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Entfernen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}
